import { BaseFormatAdapter } from 'app/model/adapters/dataflow/formatAdapters/base-format.adapter';
import { KafkaSettings } from 'app/model/dataflow/dataset-settings/kafka-settings';
import { DataFlowDSLAdapterRegistry, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowExpressionUtils } from '../expression.util';
import { BooleanDataFlowType, NumberDataFlowType, StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DslConnectorConstants } from '../../mappingArtifact/DslConnectorConstants';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(KafkaSettings)
})
export class KafkaAdapter extends BaseFormatAdapter<KafkaSettings> {
    protected async toSourceDSL(setting: KafkaSettings): Promise<string[]> {
        const props = [];
        props.push(`store: '${DatasetFormatType.Kafka}'`);
        props.push(`format: '${setting.format.expression}'`);
        if (setting.instanceSelectionType.expression === 'manual') {
            if (!!setting.subscribe?.expression) {
                props.push(`subscribe: '${setting.subscribe.expression}'`);
            }
        } else if (!!setting.subscribePattern?.expression) {
            props.push(`subscribePattern: '${setting.subscribePattern.expression}'`);
        }
        if (setting.format.expression !== 'passthrough') {
            const schemaInferenceType =
                setting.format.expression === 'avro' || !isTruthy(setting.isAutoInfer)
                    ? DslConnectorConstants.payloadSchema
                    : DslConnectorConstants.autoInferSchema;
            props.push(`schemaInferenceType: '${schemaInferenceType}'`);
            if (schemaInferenceType === DslConnectorConstants.payloadSchema && setting.payloadSchema?.expression) {
                props.push(`payloadSchema: '${setting.payloadSchema.expression}'`);
            } else if (
                schemaInferenceType === DslConnectorConstants.autoInferSchema &&
                setting.numSampleRecords?.expression
            ) {
                props.push(`numSampleRecords: ${setting.numSampleRecords.expression}`);
            }
        }
        props.push(`includeHeaders: ${setting.includeHeaders.expression}`);
        if (setting.isSinkSchemaQuery) {
            props.push(`isSinkSchemaQuery: ${setting.isSinkSchemaQuery}`);
        }
        props.push(`enableCdc: true`);
        if (setting.format.expression === 'delimited') {
            props.push(`rowDelimiter: ${DataFlowExpressionUtils.interpolateExpression('\\n')}`);
            props.push(`columnDelimiter: ${DataFlowExpressionUtils.interpolateExpression(',')}`);
            props.push(`escapeChar: ${DataFlowExpressionUtils.interpolateExpression('\\\\')}`);
            props.push(`quoteChar: ${DataFlowExpressionUtils.interpolateExpression('\\"')}`);
            props.push(`columnNamesAsHeader: true`);
        }
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<KafkaSettings> {
        const settings = this._generateSetting(owner);
        if (configs.has('format')) {
            settings.format = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('format'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('format');
        }
        if (configs.has('subscribe')) {
            settings.subscribe = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('subscribe'),
                owner,
                StringDataFlowType.TYPE
            );
            settings.instanceSelectionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                `'manual'`,
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('subscribe');
        } else if (configs.has('subscribePattern')) {
            settings.subscribePattern = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('subscribePattern'),
                owner,
                StringDataFlowType.TYPE
            );
            settings.instanceSelectionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                `'pattern'`,
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('subscribePattern');
        }
        if (configs.has('schemaInferenceType')) {
            const isAutoInfer =
                configs.get('schemaInferenceType') === `'${DslConnectorConstants.autoInferSchema}'` ? 'true' : 'false';
            settings.isAutoInfer = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                isAutoInfer,
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('schemaInferenceType');
        }
        if (settings.isAutoInfer?.expression === 'false' && configs.has('payloadSchema')) {
            settings.payloadSchema = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('payloadSchema'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('payloadSchema');
        }
        if (configs.has('numSampleRecords')) {
            settings.numSampleRecords = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('numSampleRecords'),
                owner,
                NumberDataFlowType.TYPE
            );
            handledConfigs.push('numSampleRecords');
        }
        if (configs.has('includeHeaders')) {
            settings.includeHeaders = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('includeHeaders'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('includeHeaders');
        }
        return settings;
    }
    protected async toSinkDSL(settings: KafkaSettings): Promise<string[]> {
        return [];
    }
    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<KafkaSettings> {
        return undefined;
    }
    protected _generateSetting(owner: DataFlowSource | DataFlowSink): KafkaSettings {
        const setting = Owned.create<KafkaSettings>(KafkaSettings.MODEL, owner);
        setting.initialize(undefined);
        return setting;
    }
}
